import { useTranslation } from 'react-i18next';
import { Users, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ContextualHint } from '../components';

interface TeamMember {
  id: number;
  name: string;
  email?: string;
  avatar_url?: string;
  role?: string;
}

interface TeamMemberStepProps {
  teamMembers: TeamMember[];
  selectedMemberId: number | null;
  onSelectMember: (memberId: number) => void;
  isFirstTimeFlow?: boolean;
}

export function TeamMemberStep({
  teamMembers,
  selectedMemberId,
  onSelectMember,
  isFirstTimeFlow = false,
}: TeamMemberStepProps) {
  const { t } = useTranslation('staff');

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
          <Users className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('availability.wizard.teamMember.title', 'Select Team Member')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('availability.wizard.teamMember.subtitle', 'Who will provide this service?')}
          </p>
        </div>
      </div>

      {/* Hint */}
      <ContextualHint variant="explanation">
        {isFirstTimeFlow
          ? t(
              'availability.wizard.hints.teamMemberFirstTime',
              'Select who will provide the service. Customers will see their availability when booking.'
            )
          : t(
              'availability.wizard.hints.teamMember',
              'Choose the team member whose availability you want to set. Each member can have their own schedule.'
            )}
      </ContextualHint>

      {/* Team member list */}
      <div className="space-y-2">
        {teamMembers.map((member) => {
          const isSelected = selectedMemberId === member.id;

          return (
            <button
              key={member.id}
              type="button"
              onClick={() => onSelectMember(member.id)}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all tap-highlight-transparent',
                'active:scale-[0.99]',
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300 active:bg-gray-50'
              )}
            >
              <Avatar className="h-12 w-12">
                <AvatarImage src={member.avatar_url} alt={member.name} />
                <AvatarFallback className="bg-gray-100 text-gray-600 text-sm font-medium">
                  {getInitials(member.name)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 text-left">
                <p
                  className={cn(
                    'font-medium',
                    isSelected ? 'text-blue-900' : 'text-gray-900'
                  )}
                >
                  {member.name}
                </p>
                {member.email && (
                  <p
                    className={cn(
                      'text-sm',
                      isSelected ? 'text-blue-700' : 'text-gray-500'
                    )}
                  >
                    {member.email}
                  </p>
                )}
              </div>

              {isSelected && (
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {teamMembers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {t('availability.wizard.teamMember.noMembers', 'No team members found')}
        </div>
      )}
    </div>
  );
}
